import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { CollectionData, PaginationResults, TypeQuery } from "@/lib/type";
import {
  RestaurantCustomer,
  CustomerDetails,
  CustomerSearchParams,
} from "./customer.type";

interface CustomerQuery extends TypeQuery, CustomerSearchParams {}

export const CustomerApi = createApi({
  reducerPath: "customerApi",
  tagTypes: ["customers", "customer"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getRestaurantCustomers: build.query<
      PaginationResults<RestaurantCustomer>,
      CustomerQuery
    >({
      query: ({ restaurantId, ...params }) => ({
        url: `restaurants/${restaurantId}/customers`,
        method: "GET",
        params,
      }),
      providesTags: ["customers"],
      transformResponse: (data: CollectionData<RestaurantCustomer>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getCustomerDetails: build.query<CustomerDetails, string>({
      query: (customerId) => `customers/${customerId}`,
      providesTags: ["customer"],
    }),

    exportRestaurantCustomers: build.query<Blob, string>({
      query: (restaurantId) => ({
        url: `restaurants/${restaurantId}/customers/export`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    createCustomer: build.mutation<
      RestaurantCustomer,
      {
        phone: string;
        fullName: string;
        orderId: string;
      }
    >({
      query: (data) => ({
        url: "customers",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["customers"],
    }),
  }),
});

export const {
  useGetRestaurantCustomersQuery,
  useLazyGetRestaurantCustomersQuery,
  useGetCustomerDetailsQuery,
  useLazyExportRestaurantCustomersQuery,
  useCreateCustomerMutation,
} = CustomerApi;